import React, { useState, useRef, useEffect, lazy, Suspense } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { authService } from './services/authService';
import Navbar from './components/navbar';
import LoginModel from './components/LoginModel';
import FileUploadModel from './components/FileUploadModel';
import TermsModel from './components/TermsModel';
import RefundModel from './components/RefundModel';
import PrivacyModel from './components/PrivacyModel';
import FaqModal from './components/FaqModal';
import FeedbackModal from './components/FeedbackModal';
import ContactUsModal from './components/ContactUsModal';
import EmailCollectionModal from './components/EmailCollectionModal';
import TemplateSelection from './pages/TemplateSelection';
import ResumeEditor from './pages/ResumeEditor';
import ResumeEditorV2 from './pages/ResumeEditorV2';
import AtsScorePage from './pages/AtsScorePage';
import HomePage from './pages/HomePage';
import PricingPage from './pages/PricingPage';
import ProfilePage from './pages/ProfilePage';
import BlogPage from './pages/BlogPage';
import TemplatesGallery from './pages/TemplatesGallery';
import MyFiles from './pages/MyFiles';
import CoverLetterTemplatesGallery from './pages/CoverLetterTemplatesGallery';
import CoverLetterEditor from './pages/CoverLetterEditor';
// import TestCoverLetter from './pages/TestCoverLetter'; // kept in codebase, route disabled
// import TestResumeTemplates from './pages/TestResumeTemplates'; // kept in codebase, route disabled
import AIImprovementPage from './pages/AIImprovementPage';
import AICareerToolkit from './pages/AICareerToolkit';
import AICareerMap from './pages/AICareerMap';
import MCQTest from './pages/MCQTest';
import CustomerSupport from './pages/CustomerSupport';
import { UserProvider } from './contexts/UserContext';
import MaintenancePage from './components/MaintenancePage';
import { db } from './firebase/firebaseConfig';
import { doc, onSnapshot } from 'firebase/firestore';
import './App.css';

// ═══ Admin Panel (Lazy Loaded — NOT in user bundle) ═══
const AdminLogin = lazy(() => import('./admin/pages/AdminLogin'));
const AdminLayout = lazy(() => import('./admin/components/AdminLayout'));
const AdminProtectedRoute = lazy(() => import('./admin/components/AdminProtectedRoute'));
const AdminDashboard = lazy(() => import('./admin/pages/AdminDashboard'));
const AdminUsers = lazy(() => import('./admin/pages/AdminUsers'));
const AdminReports = lazy(() => import('./admin/pages/AdminReports'));
const AdminDiscounts = lazy(() => import('./admin/pages/AdminDiscounts'));
const AdminOthers = lazy(() => import('./admin/pages/AdminOthers'));
const AdminSupportTickets = lazy(() => import('./admin/pages/AdminSupportTickets'));
const AdminDeleteResume = lazy(() => import('./admin/pages/AdminDeleteResume'));
const AdminHRSpace = lazy(() => import('./admin/pages/AdminHRSpace'));
const AdminBlog = lazy(() => import('./admin/pages/AdminBlog'));

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showFileUploadModal, setShowFileUploadModal] = useState(false);
  const [fileUploadType, setFileUploadType] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showFaqModal, setShowFaqModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showContactUsModal, setShowContactUsModal] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [useCacheForAts, setUseCacheForAts] = useState(true); // Cache control for ATS
  const [navigatingToSection, setNavigatingToSection] = useState(false); // Track section navigation
  const [maintenanceMode, setMaintenanceMode] = useState({ enabled: false, message: '' });
  const [showEmailCollectionModal, setShowEmailCollectionModal] = useState(false);

  // Refs for scroll navigation
  const homeRef = useRef(null);
  const featuresRef = useRef(null);
  const whyRef = useRef(null);
  const atsRef = useRef(null);
  const heroRef = useRef(null);
  const productRef = useRef(null);
  const coverLetterRef = useRef(null);
  const aboutRef = useRef(null);

  // Auth state listener
  useEffect(() => {
    const unsubscribe = authService.onAuthStateChange((user) => {
      if (user) {
        // User is signed in
        setIsLoggedIn(true);
        setUser(user);
      } else {
        // User is signed out
        setIsLoggedIn(false);
        setUser(null);
      }
    });

    // Cleanup listener on unmount
    return () => {
      unsubscribe();
    };
  }, []);

  // Maintenance mode listener (real-time from Firestore)
  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, 'settings', 'maintenance'),
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          setMaintenanceMode({ enabled: !!data.enabled, message: data.message || '' });
        } else {
          setMaintenanceMode({ enabled: false, message: '' });
        }
      },
      (err) => {
        console.error('Maintenance listener error:', err);
        // On error, don't block the app
        setMaintenanceMode({ enabled: false, message: '' });
      }
    );
    return () => unsub();
  }, []);

  // Check for navigation state to open login modal (from auth guards)
  useEffect(() => {
    if (location.state?.openLoginModal) {
      setShowLoginModal(true);
      
      // Clear the state to prevent reopening on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  // Scroll to top on route change (unless navigating to a specific section)
  useEffect(() => {
    if (!navigatingToSection) {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }
    // Reset the flag after navigation
    if (navigatingToSection) {
      setNavigatingToSection(false);
    }
  }, [location.pathname]);

  const handleLoginClick = () => {
    setShowLoginModal(true);
  };

  const handleLoginSuccess = async (userData) => {
    setIsLoggedIn(true);
    setUser(userData);
    setShowLoginModal(false);

    // Show email collection modal for phone users without email
    // Check both Firebase Auth email AND Firestore users collection email
    if (userData && !userData.email) {
      try {
        const { doc, getDoc } = await import('firebase/firestore');
        const userDocRef = doc(db, 'users', userData.uid);
        const userDoc = await getDoc(userDocRef);
        const firestoreEmail = userDoc.exists() ? userDoc.data().email : '';
        
        if (!firestoreEmail) {
          setTimeout(() => {
            setShowEmailCollectionModal(true);
          }, 500);
        }
      } catch (err) {
        // If Firestore check fails, show modal as fallback
        setTimeout(() => {
          setShowEmailCollectionModal(true);
        }, 500);
      }
    }
    
    // 🔄 Check if there's a returnUrl from auth guard redirect
    const returnUrl = location.state?.returnUrl;
    if (returnUrl && returnUrl !== '/') {
      navigate(returnUrl, { replace: true });
      return;
    }
    
    // 🔄 Check for pending template selection
    const pendingTemplate = sessionStorage.getItem('pendingTemplateSelection');
    if (pendingTemplate) {
      const template = JSON.parse(pendingTemplate);
      sessionStorage.removeItem('pendingTemplateSelection');
      navigate('/resume-editor-v2', { state: { selectedTemplate: template } });
      return;
    }
    
    // 🔄 Check for pending ATS upload
    const pendingAtsUpload = sessionStorage.getItem('pendingAtsUpload');
    if (pendingAtsUpload) {
      sessionStorage.removeItem('pendingAtsUpload');
      setFileUploadType('ats-check');
      setShowFileUploadModal(true);
      return;
    }
    
    // 🔄 Check if there's a postLoginRedirect from sessionStorage
    const postLoginRedirect = sessionStorage.getItem('postLoginRedirect');
    if (postLoginRedirect) {
      sessionStorage.removeItem('postLoginRedirect');
      navigate(postLoginRedirect);
    }
  };

  const handleLogout = async () => {
    try {
      const result = await authService.logoutUser();
      
      if (result.success) {
        // The auth state listener will handle updating isLoggedIn and user
        // Redirect to home page
        navigate('/');
      } else {
        console.error('❌ App.jsx: Logout failed:', result.error);
        // Force clear state even if logout failed
        setIsLoggedIn(false);
        setUser(null);
        // Still redirect to home
        navigate('/');
      }
    } catch (error) {
      console.error('❌ App.jsx: Logout error:', error);
      // Force clear state on error
      setIsLoggedIn(false);
      setUser(null);
      // Still redirect to home
      navigate('/');
    }
  };

  const handleFileUploadClick = (type) => {
    // 🔒 AUTH CHECK: Ensure user is logged in before file upload
    if (!isLoggedIn) {
      // Store the upload type for post-login action
      if (type === 'ats-check') {
        sessionStorage.setItem('pendingAtsUpload', 'true');
      }
      
      setShowLoginModal(true);
      return;
    }

    setFileUploadType(type);
    setShowFileUploadModal(true);
  };

  const handleAtsCheckComplete = (file, useCache = true) => {
    setUploadedFile(file);
    setUseCacheForAts(useCache);
    setShowFileUploadModal(false);
    navigate('/ats-score');
  };

  const handleResumeUploadComplete = (parsedDataId) => {
    setShowFileUploadModal(false);
    // Navigate to templates gallery with parsedDataId in state
    navigate('/templates', { state: { parsedDataId } });
  };

  const handleTemplatesClick = () => {
    featuresRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToHome = () => {
    navigate('/');
    setTimeout(() => {
      homeRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const scrollToFeatures = () => {
    featuresRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToWhy = () => {
    whyRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleAtsClick = () => {
    if (location.pathname !== '/') {
      // Navigate to home first, then scroll to ATS section
      setNavigatingToSection(true);
      navigate('/');
      setTimeout(() => {
        atsRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      // Already on home, just scroll
      atsRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleAboutClick = () => {
    if (location.pathname !== '/') {
      // Navigate to home first, then scroll to About section
      setNavigatingToSection(true);
      navigate('/');
      setTimeout(() => {
        aboutRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      // Already on home, just scroll
      aboutRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleCoverLetterClick = () => {
    // Check if user is logged in
    if (!isLoggedIn) {
      setShowLoginModal(true);
      // Store the intent to navigate to cover letter templates after login
      sessionStorage.setItem('postLoginRedirect', '/cover-letter-templates');
      return;
    }

    // User is logged in, navigate to cover letter templates
    navigate('/cover-letter-templates');
  };

  // Handler for "Generate Cover Letter" button from HomePage
  const handleGenerateCoverLetterClick = () => {
    // Check if user is logged in
    if (!isLoggedIn) {
      setShowLoginModal(true);
      // Store the intent to navigate to cover letter templates after login
      sessionStorage.setItem('postLoginRedirect', '/cover-letter-templates');
      return;
    }

    // User is logged in, navigate to cover letter templates
    navigate('/cover-letter-templates');
  };

  const handleAtsCheckClick = () => {
    if (location.pathname !== '/') {
      // Navigate to home first, then scroll to ATS section
      setNavigatingToSection(true);
      navigate('/');
      setTimeout(() => {
        atsRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      // Already on home, just scroll
      atsRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToSection = (ref) => {
    ref.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleGetStartedClick = () => {
    navigate('/templates');
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    navigate('/editor');
  };

  const handleBackToHome = () => {
    navigate('/');
    setSelectedTemplate(null);
  };

  const handleBackToTemplates = () => {
    navigate('/templates');
  };

  const handleResumeSave = (resumeData) => {
    // Here you would implement the save/download functionality
    alert('Resume saved successfully! Download functionality will be implemented.');
  };

  // ═══ Admin Panel Routes — Render separately (no Navbar, no modals) ═══
  if (location.pathname.startsWith('/adminonly')) {
    return (
      <Suspense fallback={<div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100vh',background:'#f8fafc'}}>Loading...</div>}>
        <Routes>
          <Route path="/adminonly/loginaccess" element={<AdminLogin />} />
          <Route path="/adminonly" element={
            <AdminProtectedRoute>
              <AdminLayout />
            </AdminProtectedRoute>
          }>
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="reports" element={<AdminReports />} />
            <Route path="discounts" element={<AdminDiscounts />} />
            <Route path="settings" element={<AdminOthers />} />
            <Route path="support-tickets" element={<AdminSupportTickets />} />
            <Route path="delete-resume" element={<AdminDeleteResume />} />
            <Route path="hr-space" element={<AdminHRSpace />} />
            <Route path="blog" element={<AdminBlog />} />
          </Route>
        </Routes>
      </Suspense>
    );
  }

  // ═══ Maintenance mode blocker (non-admin routes only) ═══
  if (maintenanceMode.enabled) {
    return <MaintenancePage message={maintenanceMode.message} />;
  }

  return (
    <UserProvider>
      <div className="App">
        {/* Navbar - Always visible on all pages */}
        <Navbar 
        currentUser={user}
        authLoading={false}
        onLoginClick={() => setShowLoginModal(true)}
        onLogout={handleLogout}
        onTemplatesClick={handleTemplatesClick}
        onHomeClick={handleBackToHome}
        onFeaturesClick={scrollToFeatures}
        onWhyClick={scrollToWhy}
        onAtsClick={handleAtsClick}
        onAboutClick={handleAboutClick}
        onCoverLetterClick={handleCoverLetterClick}
        onAtsCheckClick={handleAtsCheckClick}
      />

      {/* Page Routes */}
      <Routes>
        <Route 
          path="/" 
          element={
            <HomePage 
              isLoggedIn={isLoggedIn}
              onGetStartedClick={handleGetStartedClick}
              onFileUploadClick={handleFileUploadClick}
              onLoginClick={() => setShowLoginModal(true)}
              onGenerateCoverLetterClick={handleGenerateCoverLetterClick}
              onTermsClick={() => setShowTermsModal(true)}
              onRefundClick={() => setShowRefundModal(true)}
              onPrivacyClick={() => setShowPrivacyModal(true)}
              onFaqClick={() => setShowFaqModal(true)}
              onFeedbackClick={() => setShowFeedbackModal(true)}
              onContactUsClick={() => setShowContactUsModal(true)}
              heroRef={heroRef}
              productRef={productRef}
              whyRef={whyRef}
              atsRef={atsRef}
              coverLetterRef={coverLetterRef}
              aboutRef={aboutRef}
            />
          } 
        />
        
        <Route 
          path="/resume-editor" 
          element={<ResumeEditor />} 
        />
        
        <Route 
          path="/resume-editor-v2" 
          element={<ResumeEditorV2 />} 
        />
        
        <Route 
          path="/ats-score" 
          element={
            uploadedFile ? (
              <AtsScorePage 
                uploadedFile={uploadedFile}
                useCache={useCacheForAts}
                onBack={handleBackToHome}
              />
            ) : null
          } 
        />
        
        <Route 
          path="/pricing" 
          element={<PricingPage />} 
        />

        <Route 
          path="/profile" 
          element={<ProfilePage />} 
        />

        <Route 
          path="/my-files" 
          element={<MyFiles />} 
        />

        <Route 
          path="/blog" 
          element={<BlogPage />} 
        />

        <Route 
          path="/templates" 
          element={<TemplatesGallery />} 
        />
        
        <Route 
          path="/cover-letter-templates" 
          element={<CoverLetterTemplatesGallery />} 
        />
        
        <Route 
          path="/cover-letter-editor" 
          element={<CoverLetterEditor />} 
        />
        {/* Route disabled — TestCoverLetter page kept in codebase */}
        {/* <Route path="/test-resume-templates" element={<TestResumeTemplates />} /> */}
        
        <Route 
          path="/customer-support" 
          element={<CustomerSupport onFaqClick={() => { navigate('/'); setTimeout(() => setShowFaqModal(true), 100); }} onLoginClick={() => setShowLoginModal(true)} />} 
        />
        
        {/* AI Improvement Tools - Nested Routes */}
        <Route 
          path="/ai-improvement" 
          element={<AIImprovementPage />}
        >
          <Route path="ai-career-toolkit" element={<AICareerToolkit />} />
          <Route path="ai-career-map" element={<AICareerMap />} />
          <Route path="take-mock-interview" element={<MCQTest />} />
        </Route>
      </Routes>

      {/* Modals - Always available */}
      <LoginModel
        show={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onLoginSuccess={handleLoginSuccess}
      />

      <FileUploadModel
        show={showFileUploadModal}
        onClose={() => setShowFileUploadModal(false)}
        type={fileUploadType}
        onAtsCheckComplete={handleAtsCheckComplete}
        onResumeUploadComplete={handleResumeUploadComplete}
      />
      
      <div id="recaptcha-container"></div>
      
      <ToastContainer
        containerId="main-toast"
        position="top-center"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick={true}
        rtl={false}
        pauseOnFocusLoss={false}
        draggable={true}
        pauseOnHover={false}
        theme="light"
      />

      {/* Modal Components */}
      <TermsModel 
        show={showTermsModal} 
        onClose={() => setShowTermsModal(false)} 
      />
      
      <RefundModel 
        show={showRefundModal} 
        onClose={() => setShowRefundModal(false)} 
      />
      
      <PrivacyModel 
        show={showPrivacyModal} 
        onClose={() => setShowPrivacyModal(false)} 
      />
      
      <FaqModal 
        show={showFaqModal} 
        onClose={() => setShowFaqModal(false)} 
      />
      
      <FeedbackModal 
        show={showFeedbackModal} 
        onClose={() => setShowFeedbackModal(false)} 
      />
      
      <ContactUsModal 
        show={showContactUsModal} 
        onClose={() => setShowContactUsModal(false)} 
      />

      {/* Email Collection Modal - shows after phone/MSG91 login */}
      <EmailCollectionModal
        show={showEmailCollectionModal}
        onClose={() => setShowEmailCollectionModal(false)}
        onEmailUpdated={(email) => {
          setShowEmailCollectionModal(false);
        }}
      />
    </div>
    </UserProvider>
  );
}

export default App;